/**
 * An audit entry, as an administrator reads it.
 *
 * The server stores what happened as an action code and two JSON snapshots,
 * because that is the record: stable, append-only, and not tied to how any
 * screen words it. This turns one into a title ("Plan assigned"), who did it,
 * and the fields that changed ("Plan · Free → Premium"), using only what the
 * entry actually holds. A field this file has no label for is left to the raw
 * view rather than guessed at.
 *
 * The shapes below are the ones the API writes today — `repositories/
 * management.ts`, `modules/subscriptions/admin.ts`, `index.ts` and
 * `entitlements.ts`. A new shape still renders: its title falls back to the
 * generic one, its unknown fields are skipped, and the raw JSON is one click
 * away.
 */
import {
  formatList,
  formatNumber,
  type Language,
  type Translate,
  type TranslationKey,
} from '@athena/i18n'
import type { ChangeListItem } from '@athena/ui'

import {
  formatDate,
  languageName,
  planName,
  roleName,
  sortRoles,
  type Role,
} from '../../lib/display.js'

export interface AuditEntryInput {
  id: string
  actorName: string | null
  actorEmail?: string | null
  action: string
  reason: string | null
  before: string | null
  after: string | null
}

export interface AuditDescription {
  title: string
  actor: string
  changes: ChangeListItem[]
}

type Json = Record<string, unknown>
type Format = (value: unknown, t: Translate, language: Language) => string | null

const plan: Format = (value, t) =>
  typeof value === 'string' ? planName(t, value) : t('admin.auditValueNone')
const date: Format = (value, t, language) =>
  typeof value === 'string' ? formatDate(value, language) : t('admin.noEndDate')
const text: Format = (value) => (typeof value === 'string' ? value : null)
const number: Format = (value, _, language) =>
  typeof value === 'number' ? formatNumber(value, language) : null
const yesNo: Format = (value, t) =>
  typeof value === 'boolean' ? t(value ? 'admin.auditValueYes' : 'admin.auditValueNo') : null
/** A list of ids the entry recorded: how many, which is what an administrator can use. */
const count: Format = (value, _, language) =>
  Array.isArray(value) && value.length > 0 ? formatNumber(value.length, language) : null

/**
 * An upgrade request's status, in words. The API writes DECLINED today; the
 * others are the rest of the column's values, named so a future entry does
 * not reach the screen as a code. Anything newer still does, rather than
 * being hidden.
 */
function requestStatusName(t: Translate, status: string): string {
  switch (status) {
    case 'DECLINED':
      return t('admin.auditValueDeclined')
    case 'APPROVED':
      return t('admin.auditValueApproved')
    case 'PENDING':
      return t('admin.auditValuePending')
    case 'WITHDRAWN':
      return t('admin.auditValueWithdrawn')
    default:
      return status
  }
}

interface Field {
  label: TranslationKey
  format: Format
  /** Shown only when the entry marks it true — "the name changed" — and never as a before/after. */
  flag?: boolean
}

const FIELDS: Readonly<Record<string, Field>> = {
  flag: {
    label: 'admin.auditFieldFeatureFlag',
    format: (value, t) =>
      value === 'PRACTICE_GENERATION_PROVIDER_OVERRIDE'
        ? t('management.practiceProviderOverrideFlag')
        : value === 'LOCAL_DEVELOPMENT'
          ? t('management.localDevelopmentFlag')
          : typeof value === 'string'
            ? value
            : null,
  },
  flags: {
    label: 'management.featureFlags',
    format: (value, t, language) => {
      if (!Array.isArray(value)) return null
      if (value.length === 0) return t('admin.auditValueNone')
      return formatList(
        value.map((flag) =>
          flag === 'PRACTICE_GENERATION_PROVIDER_OVERRIDE'
            ? t('management.practiceProviderOverrideFlag')
            : flag === 'LOCAL_DEVELOPMENT'
              ? t('management.localDevelopmentFlag')
              : String(flag),
        ),
        language,
      )
    },
  },
  enabled: { label: 'admin.auditFieldEnabled', format: yesNo },
  planCode: { label: 'admin.auditFieldPlan', format: plan },
  effectivePlanCode: { label: 'admin.auditFieldPlanInForce', format: plan },
  requestedPlanCode: { label: 'admin.auditFieldRequestedPlan', format: plan },
  scheduledPlanCode: { label: 'admin.auditFieldScheduledPlan', format: plan },
  accessEndsAt: { label: 'admin.auditFieldAccessEnds', format: date },
  periodAnchorAt: { label: 'admin.auditFieldAnchor', format: date },
  scheduledEffectiveAt: { label: 'admin.auditFieldScheduledAt', format: date },
  effectiveAt: { label: 'admin.auditFieldScheduledAt', format: date },
  disabled: {
    label: 'admin.auditFieldAccess',
    format: (value, t) =>
      typeof value === 'boolean' ? t(value ? 'admin.statusDisabled' : 'admin.statusActive') : null,
  },
  tutor: { label: 'admin.auditFieldTutor', format: yesNo },
  student: { label: 'admin.auditFieldStudent', format: yesNo },
  admin: { label: 'admin.auditFieldAdmin', format: yesNo },
  preferredLanguage: {
    label: 'admin.auditFieldLanguage',
    format: (value, t) => (typeof value === 'string' ? languageName(t, value) : null),
  },
  roles: {
    label: 'admin.auditFieldProfiles',
    format: (value, t, language) =>
      Array.isArray(value)
        ? value.length === 0
          ? t('admin.noProfile')
          : formatList(
              sortRoles(value as Role[]).map((role) => roleName(t, role)),
              language,
            )
        : null,
  },
  emailVerified: {
    label: 'admin.auditFieldEmail',
    format: (value, t) =>
      typeof value === 'boolean'
        ? t(value ? 'admin.auditValueVerified' : 'admin.auditValueUnverified')
        : null,
  },
  code: { label: 'admin.auditFieldCode', format: text },
  active: {
    label: 'admin.auditFieldSubjectStatus',
    format: (value, t) =>
      typeof value === 'boolean'
        ? t(value ? 'admin.subjectStatusActive' : 'admin.subjectStatusInactive')
        : null,
  },
  spanishName: { label: 'admin.auditFieldSpanishName', format: text },
  englishName: { label: 'admin.auditFieldEnglishName', format: text },
  consumed: { label: 'admin.auditFieldCreditsUsed', format: number },
  remaining: { label: 'admin.auditFieldCreditsLeft', format: number },
  amount: {
    label: 'admin.auditFieldAdjustment',
    format: (value, _, language) =>
      typeof value === 'number' ? formatNumber(value, language, { signed: true }) : null,
  },
  status: {
    label: 'admin.auditFieldRequestStatus',
    format: (value, t) => (typeof value === 'string' ? requestStatusName(t, value) : null),
  },
  suspendedStudents: { label: 'admin.auditFieldStudentsSetAside', format: count },
  suspendedPractices: { label: 'admin.auditFieldPracticesSetAside', format: count },
  revokedInvitations: { label: 'admin.auditFieldInvitationsRevoked', format: count },
  nameChanged: { label: 'admin.auditFieldName', format: yesNo, flag: true },
  profileNamesChanged: { label: 'admin.auditFieldProfileNames', format: yesNo, flag: true },
  deleted: { label: 'admin.auditFieldRecord', format: yesNo, flag: true },
}

/** Parsed, with a subject's translations spread into the two names they are. */
function snapshot(raw: string | null): Json {
  if (!raw) return {}
  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return {}
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const record = { ...(value as Json) }
  const translations = record['translations']
  if (Array.isArray(translations)) {
    for (const entry of translations as { locale?: unknown; name?: unknown }[]) {
      if (typeof entry.locale !== 'string') continue
      if (entry.locale.startsWith('es')) record['spanishName'] = entry.name
      if (entry.locale.startsWith('en')) record['englishName'] = entry.name
    }
    delete record['translations']
  }
  return record
}

function titleKey(action: string, after: Json): TranslationKey {
  switch (action) {
    case 'user.created':
      return 'admin.auditActionUserCreated'
    case 'user.update':
      return 'admin.auditActionUserUpdated'
    case 'user.featureFlags.updated':
      return 'admin.auditActionUserFeatureFlagsUpdated'
    case 'user.featureFlag.updated':
      return 'admin.auditActionUserFeatureFlagUpdated'
    case 'user.access':
      return after['disabled'] === true
        ? 'admin.auditActionUserDisabled'
        : after['disabled'] === false
          ? 'admin.auditActionUserReactivated'
          : 'admin.auditActionUserUpdated'
    case 'user.deleted':
      return 'admin.auditActionUserDeleted'
    case 'subject.created':
      return 'admin.auditActionSubjectCreated'
    case 'subject.updated':
      return 'admin.auditActionSubjectUpdated'
    case 'subject.deleted':
      return 'admin.auditActionSubjectDeleted'
    case 'subscription.plan.assigned':
      return 'admin.auditActionPlanAssigned'
    case 'subscription.change.scheduled':
      return 'admin.auditActionChangeScheduled'
    case 'subscription.access.ended':
      return 'admin.auditActionAccessEnded'
    case 'subscription.upgradeRequest.approved':
      return 'admin.auditActionRequestApproved'
    case 'subscription.upgradeRequest.declined':
      return 'admin.auditActionRequestDeclined'
    case 'subscription.limits.enforced':
      return 'admin.auditActionLimitsEnforced'
    case 'subscription.downgrade.scheduled':
      return 'admin.auditActionDowngradeScheduled'
    case 'subscription.downgrade.cancelled':
      return 'admin.auditActionDowngradeCancelled'
    case 'subscription.aiUsage.adjusted':
      return 'admin.auditActionUsageChanged'
    default:
      return action.startsWith('subscription.')
        ? 'admin.auditActionSubscriptionChanged'
        : 'admin.auditActionGeneric'
  }
}

/**
 * Who made the change. An entry names an administrator when one made it;
 * when none is named, the action says who did — the tutor on their own plan
 * screen, or the clock reaching a date nobody had to press anything for — and
 * only then is it an administrator whose account has since gone.
 */
function actorText(entry: AuditEntryInput, t: Translate): string {
  if (entry.actorName && entry.actorEmail) return `${entry.actorName} (${entry.actorEmail})`
  if (entry.actorName || entry.actorEmail) return (entry.actorName ?? entry.actorEmail)!
  if (entry.action === 'subscription.limits.enforced') return t('admin.auditActorAutomatic')
  if (entry.action.startsWith('subscription.downgrade.')) return t('admin.auditActorTutor')
  return t('admin.auditDeletedActor')
}

export function describeAudit(
  entry: AuditEntryInput,
  t: Translate,
  language: Language,
): AuditDescription {
  const before = snapshot(entry.before)
  const after = snapshot(entry.after)
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  const changes: ChangeListItem[] = []

  for (const key of keys) {
    const field = FIELDS[key]
    if (!field) continue
    if (field.flag) {
      if (after[key] === true)
        changes.push({
          id: key,
          label: t(field.label),
          after: t(key === 'deleted' ? 'admin.auditValueDeleted' : 'admin.auditValueChanged'),
        })
      continue
    }
    const hadBefore = key in before
    const hasAfter = key in after
    const from = hadBefore ? field.format(before[key], t, language) : null
    const to = hasAfter ? field.format(after[key], t, language) : null
    if (from === null && to === null) continue
    // A snapshot field that did not change is the record being thorough,
    // not something that happened.
    if (hadBefore && hasAfter && from === to) continue
    changes.push({
      id: key,
      label: t(field.label),
      ...(from === null ? {} : { before: from }),
      ...(to === null ? {} : { after: to }),
    })
  }

  return {
    title: t(titleKey(entry.action, after)),
    actor: actorText(entry, t),
    changes,
  }
}
